/**
 * Hub ↔ next-email-client calendar bridge (same contract as CRM inbox / professional-email routes).
 * Uses NEXT_EMAIL_CLIENT_ORIGIN + HUB_INBOX_API_SECRET (or NEXT_EMAIL_CLIENT_HUB_SECRET).
 */

const ORIGIN = typeof process !== "undefined" && process.env ? process.env.NEXT_EMAIL_CLIENT_ORIGIN || "" : "";
const SECRET = (
  typeof process !== "undefined" && process.env
    ? process.env.HUB_INBOX_API_SECRET || process.env.NEXT_EMAIL_CLIENT_HUB_SECRET || ""
    : ""
).trim();

export interface ProfessionalEmailCalendarEvent {
  eventId: string;
  userId: string;
  title: string;
  startUtc: string;
  endUtc: string;
  description?: string;
  location?: string;
  allDay: boolean;
  timezone?: string;
  source: string;
}

interface ProfessionalEmailCalendarResponse {
  events: ProfessionalEmailCalendarEvent[];
}

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (SECRET) {
    headers["X-Hub-Inbox-Secret"] = SECRET;
  }
  return headers;
}

export function hasProfessionalEmailCalendarConfig(): boolean {
  return Boolean(ORIGIN && SECRET);
}

export function getProfessionalEmailClientOrigin(): string {
  return ORIGIN.replace(/\/$/, "");
}

export type ProfessionalEmailCalendarBridgeStatus =
  | { status: "not_configured"; message: string }
  | { status: "connected"; eventCountSample: number }
  | { status: "user_not_resolved"; message: string }
  | { status: "error"; message: string };

export async function checkProfessionalEmailCalendarBridgeStatus(params: {
  email: string;
}): Promise<ProfessionalEmailCalendarBridgeStatus> {
  if (!hasProfessionalEmailCalendarConfig()) {
    return {
      status: "not_configured",
      message:
        "WTC Email calendar is not configured (missing NEXT_EMAIL_CLIENT_ORIGIN or shared secret on the Hub server).",
    };
  }

  const now = new Date();
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - 1);
  from.setUTCHours(0, 0, 0, 0);
  const to = new Date(now);
  to.setUTCDate(to.getUTCDate() + 1);
  to.setUTCHours(23, 59, 59, 999);

  const url = new URL(`${ORIGIN}/api/hub/calendar/events`);
  url.searchParams.set("email", params.email.toLowerCase().trim());
  url.searchParams.set("fromUtc", from.toISOString());
  url.searchParams.set("toUtc", to.toISOString());

  let response: Response;
  try {
    response = await fetch(url.toString(), { headers: getHeaders() });
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Network error";
    return {
      status: "error",
      message: `Could not reach WTC Email calendar service: ${msg}`,
    };
  }

  if (response.status === 400 || response.status === 404) {
    return {
      status: "user_not_resolved",
      message:
        "The mail app could not resolve your Hub email to a user. Use the same email in WTC Email (mail), or ask an admin to confirm your app_users record exists in the mail app database.",
    };
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    return {
      status: "error",
      message: `WTC Email calendar returned ${response.status}${text ? `: ${text.slice(0, 200)}` : ""}`,
    };
  }

  const data = (await response.json()) as ProfessionalEmailCalendarResponse;
  const events = Array.isArray(data.events) ? data.events : [];
  return { status: "connected", eventCountSample: events.length };
}

/**
 * Raw fetch for CRM routes. 400/404 → []; other errors throw.
 */
export async function fetchProfessionalEmailCalendarEventsByEmail(params: {
  email: string;
  fromUtc: string;
  toUtc: string;
}): Promise<ProfessionalEmailCalendarEvent[]> {
  if (!hasProfessionalEmailCalendarConfig()) return [];

  const url = new URL(`${ORIGIN}/api/hub/calendar/events`);
  url.searchParams.set("email", params.email);
  url.searchParams.set("fromUtc", params.fromUtc);
  url.searchParams.set("toUtc", params.toUtc);

  const response = await fetch(url.toString(), {
    headers: getHeaders(),
  });

  if (!response.ok) {
    if (response.status === 400 || response.status === 404) return [];
    const text = await response.text();
    throw new Error(`WTC Email calendar API error: ${response.status} ${text}`);
  }

  const data = (await response.json()) as ProfessionalEmailCalendarResponse;
  return Array.isArray(data.events) ? data.events : [];
}

export type ProfessionalEmailCalendarResolution =
  | { resolved: true; events: ProfessionalEmailCalendarEvent[] }
  | { resolved: false };

/**
 * For availability: if the mail app resolves the user, returned events (maybe empty) are authoritative
 * for that user's WTC Email calendar. If not resolved, caller should try other sources (e.g. SA Google).
 */
export async function fetchProfessionalEmailCalendarWithResolution(params: {
  email: string;
  fromUtc: string;
  toUtc: string;
}): Promise<ProfessionalEmailCalendarResolution> {
  if (!hasProfessionalEmailCalendarConfig()) {
    return { resolved: false };
  }

  const url = new URL(`${ORIGIN}/api/hub/calendar/events`);
  url.searchParams.set("email", params.email.toLowerCase().trim());
  url.searchParams.set("fromUtc", params.fromUtc);
  url.searchParams.set("toUtc", params.toUtc);

  let response: Response;
  try {
    response = await fetch(url.toString(), { headers: getHeaders() });
  } catch {
    return { resolved: false };
  }

  if (response.status === 400 || response.status === 404) {
    return { resolved: false };
  }

  if (!response.ok) {
    return { resolved: false };
  }

  const data = (await response.json()) as ProfessionalEmailCalendarResponse;
  const events = Array.isArray(data.events) ? data.events : [];
  return { resolved: true, events };
}

/**
 * Map WTC Email events into Google Calendar–shaped items for filterAvailableSlots / existing CRM code.
 */
export function mapProfessionalEmailEventsToUnifiedShape(
  events: ProfessionalEmailCalendarEvent[],
  userEmail: string
): Array<{
  id: string;
  summary: string;
  start: { dateTime: string; timeZone?: string };
  end: { dateTime: string; timeZone?: string };
  source: string;
}> {
  return events.map((e) => ({
    id: e.eventId,
    summary: e.title,
    start: { dateTime: e.startUtc, timeZone: e.timezone },
    end: { dateTime: e.endUtc, timeZone: e.timezone },
    source: userEmail,
  }));
}

export type CreateProfessionalEmailCalendarEventResult =
  | { ok: true; event: ProfessionalEmailCalendarEvent }
  | { ok: false; reason: string };

/**
 * Create an event on the host's WTC Email (next-email-client) calendar via the Hub shared secret.
 * Used when public booking falls back to SES/ICS so the host sees the slot in the mail-app UI.
 */
export async function createProfessionalEmailCalendarEventViaHub(params: {
  hostEmail: string;
  title: string;
  startUtc: string;
  endUtc: string;
  description?: string;
  location?: string;
  attendeeEmails?: string[];
  allDay?: boolean;
  timezone?: string;
  meetLink?: string;
}): Promise<CreateProfessionalEmailCalendarEventResult> {
  if (!hasProfessionalEmailCalendarConfig()) {
    return { ok: false, reason: "not_configured" };
  }

  const origin = getProfessionalEmailClientOrigin();
  const url = `${origin}/api/hub/calendar/events`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        email: params.hostEmail.toLowerCase().trim(),
        title: params.title,
        startUtc: params.startUtc,
        endUtc: params.endUtc,
        description: params.description,
        location: params.location,
        attendeeEmails: params.attendeeEmails,
        allDay: params.allDay ?? false,
        timezone: params.timezone,
        meetLink: params.meetLink,
      }),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, reason: msg };
  }

  if (!response.ok) {
    const text = await response.text();
    return { ok: false, reason: `HTTP ${response.status}: ${text}` };
  }

  const data = (await response.json()) as { event?: ProfessionalEmailCalendarEvent };
  if (!data?.event?.eventId) {
    return { ok: false, reason: "invalid_response" };
  }

  return { ok: true, event: data.event };
}
